import React, { useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import {
  cancelInviteMemberAppLink,
  getMemberAppLinkHistory,
  unlinkMemberAppLink,
} from '../../api/memberAppLink';
import AppLinkModal from '../temporaryUser/AppLinkModal';
import UnlinkConfirmModal from './UnlinkConfirmModal';
import { LinkButton, LinkChip, StatusRow } from './MemberAppLinkStyled';

const STATUS_LINKED = 'LINKED';
const STATUS_INVITED = 'INVITED';

const toDay = value => (value ? String(value).slice(0, 10) : '');

/**
 * 회원·잠재회원 상세의 앱 연동 상태. 연동 전에는 [앱 연동], 초대 중에는 [초대 취소], 연동 후에는 '연동됨' 뱃지와 [연동 해제].
 * 아이디·연동일은 길어서 칩에 마우스를 올렸을 때만 보인다(2026-10-20).
 * 잠재회원은 신규 가입이 저장할 때 계정을 만드는 폼 값이라, [앱 연동] 버튼은 회원에서만 보인다.
 * target: {type: 'MEMBER' | 'TEMPORARY_USER', id, branchId, name, phoneNumber}
 */
/**
 * 그 지점(없으면 로그인 지점)의 현재 연동·초대. 회원 상세 기본 정보 창이 아이디·비밀번호 칸을 보일지도 이걸로 정한다.
 */
export const useBranchAppLink = target => {
  const isMember = target.type === 'MEMBER';
  // 연동은 지점 단위. 화면이 지점을 못 넘기면 지금 보고 있는 지점(로그인 지점) 기준으로 본다
  const currentBranchId = useSelector(state => state.user?.user?.branch?.branchId);
  const branchId = target.branchId ?? currentBranchId;
  const { data: allHistory = [] } = useQuery({
    queryKey: ['memberAppLinkHistory', target.type, target.id],
    queryFn: () =>
      getMemberAppLinkHistory(isMember ? { targetMemberId: target.id } : { targetTemporaryUserId: target.id }),
    enabled: !!target.id && branchId != null,
  });

  // 앱 연동은 지점 단위라 그 지점의 이력만 본다(지점을 모르면 아래에서 아무것도 그리지 않는다)
  const history = allHistory.filter(item => Number(item.branchId) === Number(branchId));

  // 이력에서 가장 최근에 처리된 LINKED 한 건이 현재 연동이다.
  const currentLink = history
    .filter(item => item.status === STATUS_LINKED)
    .sort((a, b) => String(b.processedDate ?? '').localeCompare(String(a.processedDate ?? '')))[0];

  const currentInvite = currentLink
    ? undefined
    : history.find(item => item.status === STATUS_INVITED);

  return { branchId, currentLink, currentInvite };
};

const AppLinkStatus = ({ target }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [isUnlinkConfirmOpen, setIsUnlinkConfirmOpen] = useState(false);
  const [isCancelInviteConfirmOpen, setIsCancelInviteConfirmOpen] = useState(false);

  const isMember = target.type === 'MEMBER';
  const { branchId, currentLink, currentInvite } = useBranchAppLink(target);

  const unlinkMutation = useMutation({
    mutationFn: () => unlinkMemberAppLink(currentLink.id),
    onSuccess: () => {
      toast.success('연동을 해제했습니다.');
      queryClient.invalidateQueries({ queryKey: ['memberAppLinkHistory'] });
      // 잠재회원은 연결 계정이 실제로 풀리므로 판매등록이 옛 계정을 넘기지 않게 다시 받는다.
      queryClient.invalidateQueries({ queryKey: ['temporaryUser'] });
      setIsUnlinkConfirmOpen(false);
    },
  });

  const cancelInviteMutation = useMutation({
    mutationFn: () => cancelInviteMemberAppLink(currentInvite.id),
    onSuccess: () => {
      toast.success('초대를 취소했습니다.');
      queryClient.invalidateQueries({ queryKey: ['memberAppLinkHistory'] });
      queryClient.invalidateQueries({ queryKey: ['memberAppLinks'] });
      setIsCancelInviteConfirmOpen(false);
    },
  });

  const isCancelingInvite = useRef(false);
  const runCancelInviteMutation = () => {
    if (isCancelingInvite.current) return;
    isCancelingInvite.current = true;
    cancelInviteMutation.mutate(undefined, { onSettled: () => (isCancelingInvite.current = false) });
  };

  // 더블클릭이 리렌더 전에 두 번 들어오는 것을 막는다(isPending 은 리렌더 후에야 반영된다).
  const isUnlinking = useRef(false);
  const runUnlinkMutation = () => {
    if (isUnlinking.current) return;
    isUnlinking.current = true;
    unlinkMutation.mutate(undefined, { onSettled: () => (isUnlinking.current = false) });
  };

  if (branchId == null) return null;

  return (
    <StatusRow>
      {currentLink ? (
        <>
          <Tooltip
            arrow
            title={`아이디 ${currentLink.appAccount?.username ?? '-'}${
              currentLink.processedDate ? ` · ${toDay(currentLink.processedDate)} 연동` : ''
            }`}
          >
            <LinkChip linked>
              <CheckCircleIcon sx={{ fontSize: 15 }} />연동됨
            </LinkChip>
          </Tooltip>
          <LinkButton type="button" danger onClick={() => setIsUnlinkConfirmOpen(true)}>
            연동 해제
          </LinkButton>
        </>
      ) : currentInvite ? (
        <>
          <Tooltip
            arrow
            title={`아이디 ${currentInvite.appAccount?.username ?? '-'}${
              currentInvite.registeredDate ? ` · ${toDay(currentInvite.registeredDate)} 초대` : ''
            }`}
          >
            <LinkChip>초대 중</LinkChip>
          </Tooltip>
          <LinkButton type="button" danger onClick={() => setIsCancelInviteConfirmOpen(true)}>
            초대 취소
          </LinkButton>
        </>
      ) : (
        isMember && (
          // 연동은 지점 단위다. 이 지점에 연동이 없으면 앱 계정이 있어도 [앱 연동] → 신규 계정 / 기존 계정
          <LinkButton type="button" onClick={() => setIsLinkModalOpen(true)}>
            앱 연동
          </LinkButton>
        )
      )}
      <AppLinkModal open={isLinkModalOpen} onClose={() => setIsLinkModalOpen(false)} target={target} />
      <UnlinkConfirmModal
        mode="cancelInvite"
        open={isCancelInviteConfirmOpen}
        onClose={() => setIsCancelInviteConfirmOpen(false)}
        onConfirm={runCancelInviteMutation}
        isMember={isMember}
        account={{
          name: target.name,
          username: currentInvite?.appAccount?.username,
          date: toDay(currentInvite?.registeredDate),
        }}
        isLoading={cancelInviteMutation.isPending}
      />
      <UnlinkConfirmModal
        open={isUnlinkConfirmOpen}
        onClose={() => setIsUnlinkConfirmOpen(false)}
        onConfirm={runUnlinkMutation}
        isMember={isMember}
        account={{
          name: target.name,
          username: currentLink?.appAccount?.username,
          date: toDay(currentLink?.processedDate),
        }}
        isLoading={unlinkMutation.isPending}
      />
    </StatusRow>
  );
};

export default AppLinkStatus;
